import { init, setContext } from "@bk/app-events";
import { useAuth, type UserMe } from "@/store/authStore";
import { isTelegramEnv } from "@/utils/helpers";
import { startParamForAnalytics } from "@/utils/startParam";
import type { Trigger } from "./triggers";

export { dataTrigger, track } from "./triggers";

const OPEN: Trigger = "bkn.app.open";
const OPENED_KEY = "bkn:analytics:opened";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content"];

function detectOs(): string {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(userAgent)) return "ios";
  if (userAgent.includes("android")) return "android";
  if (userAgent.includes("mac os x")) return "macos";
  if (userAgent.includes("windows")) return "windows";
  if (userAgent.includes("linux")) return "linux";

  return "other";
}

/** telegram_id известен до входа: из данных запуска мини-аппа или из параметра ссылки. */
function telegramIdFromLaunch(search: URLSearchParams): string | null {
  const fromLink = search.get("telegram_id");
  if (fromLink) return fromLink;

  try {
    const launch = new URLSearchParams(window.location.hash.slice(1));
    const user = new URLSearchParams(launch.get("tgWebAppData") ?? "").get(
      "user"
    );
    const id = user ? (JSON.parse(user) as { id?: unknown }).id : null;

    return id === null || id === undefined ? null : String(id);
  } catch {
    return null;
  }
}

function launchContext(): Record<string, string> {
  const search = new URLSearchParams(window.location.search);
  const context: Record<string, string> = {
    platform: isTelegramEnv() ? "tgapp" : "web",
    os: detectOs(),
  };

  const telegramId = telegramIdFromLaunch(search);
  if (telegramId) context.telegram_id = telegramId;

  const startParam =
    search.get("tgWebAppStartParam") ?? search.get("startapp");
  if (startParam) context.start_param = startParamForAnalytics(startParam);

  UTM_KEYS.forEach((key) => {
    const value = search.get(key);
    if (value) context[key] = value;
  });

  return context;
}

/**
 * Открытие — один раз на запуск приложения. Страница перезагружается и внутри запуска (возврат
 * с оплаты, «На главную» с экрана установки) — такие загрузки открытием не считаем.
 */
function isFirstLoadInSession(): boolean {
  try {
    if (window.sessionStorage.getItem(OPENED_KEY) === "1") return false;
    window.sessionStorage.setItem(OPENED_KEY, "1");
  } catch {
    // sessionStorage недоступен — считаем каждую загрузку открытием
  }

  return true;
}

/** Пользователь в отчётах — telegram_id в контексте; у вошедших по почте его нет. */
function identify(me: UserMe | null) {
  if (me?.telegram_id === null || me?.telegram_id === undefined) return;

  setContext({ telegram_id: String(me.telegram_id) });
}

/**
 * Трекинг открытий и нажатий (app-events). Работает только в сборке с ключом сервиса —
 * то есть на проде: dev-стенд и локальный запуск событий не шлют.
 */
export function initAnalytics() {
  if (!__APP_EVENTS_KEY__) return;

  init({
    app: "bkn",
    key: __APP_EVENTS_KEY__,
    context: launchContext(),
    triggers: isFirstLoadInSession() ? [OPEN] : [],
    dev: __IS_DEV__,
  });

  identify(useAuth.getState().me);
  useAuth.subscribe((state, previous) => {
    if (state.me !== previous.me) identify(state.me);
  });
}
